// Radha — loads Razorpay's Checkout.js on demand (never bundled — it's a
// third-party script that must come straight from Razorpay's own CDN for
// their fraud/risk signals to work) and opens it as a promise.
declare global {
  interface Window { Razorpay?: new (opts: Record<string, unknown>) => { open: () => void }; }
}

let loading: Promise<void> | null = null;

function loadCheckoutScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load Razorpay checkout — check your connection"));
    document.head.appendChild(s);
  });
  return loading;
}

export interface RazorpayOrder {
  order_id: string;
  amount: number; // paise
  currency: string;
  key_id: string;
  invoice_number: string;
  customer_name: string;
}

/** Opens the Razorpay Checkout modal; resolves when the user completes the
 * payment flow client-side (this is NOT proof of a captured payment — only
 * the webhook-confirmed `payments` row is; the caller should poll that). */
export async function openRazorpayCheckout(order: RazorpayOrder, tenantName: string): Promise<void> {
  await loadCheckoutScript();
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay!({
      key: order.key_id,
      order_id: order.order_id,
      amount: order.amount,
      currency: order.currency,
      name: tenantName,
      description: `Invoice ${order.invoice_number}`,
      prefill: { name: order.customer_name },
      theme: { color: "#7C5CFC" },
      handler: () => resolve(),
      modal: { ondismiss: () => reject(new Error("DISMISSED")) },
    });
    rzp.open();
  });
}
